import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { BarChart3, Loader2, RefreshCw, PlayCircle, Compass } from 'lucide-react';

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-lg bg-[hsl(var(--surface-2))] p-2.5 text-center">
      <p className="font-display text-xl font-700" style={accent ? { color: 'hsl(var(--primary))' } : {}}>{value}</p>
      <p className="text-[10px] text-[hsl(var(--muted-foreground))]">{label}</p>
    </div>
  );
}

export default function TourAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/analytics/tour');
      setData(res.data);
    } catch (e) { if (e?.response?.status !== 403) console.debug('tour analytics load failed', e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5 flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]" data-testid="tour-analytics-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading tour analytics…
      </div>
    );
  }
  if (!data) return null;

  const v = data.video || {};
  const g = data.guided || {};

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-card p-5" data-testid="tour-analytics">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-xl grid place-items-center shrink-0" style={{ background: 'hsl(var(--accent))' }}><BarChart3 className="h-5 w-5" style={{ color: 'hsl(var(--primary))' }} /></div>
        <div className="flex-1">
          <div className="flex items-center gap-2"><h3 className="font-display font-600">Tour analytics</h3><Badge variant="secondary" className="text-[10px]">Owner</Badge></div>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">How new users engage with onboarding — watch vs skip.</p>
        </div>
        <Button size="sm" variant="ghost" className="rounded-lg h-8 text-xs gap-1.5" onClick={load} data-testid="tour-analytics-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Refresh
        </Button>
      </div>

      {/* Video tour */}
      <div className="rounded-xl border border-[hsl(var(--border))] p-3 mb-3" data-testid="tour-analytics-video">
        <div className="flex items-center gap-2 mb-2.5">
          <PlayCircle className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />
          <span className="text-sm font-600">Video tour</span>
          <Badge variant="outline" className="text-[10px] ml-auto" data-testid="tour-video-watch-rate">{v.watch_rate ?? 0}% watched</Badge>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <Stat label="shown" value={v.shown ?? 0} />
          <Stat label="watched" value={v.watched ?? 0} accent />
          <Stat label="skipped" value={v.skipped ?? 0} />
          <Stat label="manual opens" value={v.opened_manual ?? 0} />
        </div>
      </div>

      {/* Guided tour */}
      <div className="rounded-xl border border-[hsl(var(--border))] p-3" data-testid="tour-analytics-guided">
        <div className="flex items-center gap-2 mb-2.5">
          <Compass className="h-4 w-4" style={{ color: 'hsl(var(--primary))' }} />
          <span className="text-sm font-600">Guided tour</span>
          <Badge variant="outline" className="text-[10px] ml-auto" data-testid="tour-guided-completion-rate">{g.completion_rate ?? 0}% completed</Badge>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Stat label="shown" value={g.shown ?? 0} />
          <Stat label="completed" value={g.completed ?? 0} accent />
          <Stat label="skipped" value={g.skipped ?? 0} />
        </div>
      </div>

      {(v.shown ?? 0) === 0 && (g.shown ?? 0) === 0 && (
        <p className="text-sm text-[hsl(var(--muted-foreground))] py-4 text-center">No tour activity yet — data appears as new users go through onboarding.</p>
      )}
    </div>
  );
}
